import { registerIntegration } from "@p0security/shared/integrations/registry";
import {
  type AnyFilterSchema,
  Registration,
} from "@p0security/shared/integrations/types";

import { GoogleDriveAiAccesses } from "./accesses.ts";
import { GoogleDriveAiComponents } from "./components.ts";
import { GoogleDriveAiIconUrl } from "./icon.ts";

/** Filter ids are the access parameter names, per the house rule that a filter
 * id names the parameter it constrains rather than the target's API vocabulary.
 *
 * Two target filterables rather than one with a discriminant, because the drive
 * and the item inside it are two parameters. `resource` keys on its id alone:
 * a Drive item's name is neither unique nor stable, since anyone with write
 * access can rename a folder.
 *
 * One trap the docs have to state: `drive.name` is stale until the next verify,
 * so rules should key on `drive.id`.
 */
const GoogleDriveAiFilters = {
  drive: { keys: ["id", "name"] },
  resource: { keys: ["id"] },
  role: { keys: ["id"] },
} as const satisfies AnyFilterSchema;
export type GoogleDriveAiFilters = typeof GoogleDriveAiFilters;

export const GoogleDriveAiRegistration = Registration({
  accesses: GoogleDriveAiAccesses,
  category: "resource",
  components: GoogleDriveAiComponents,
  docs: {
    install: "agentic-gateway/mcp-server/google-drive",
    request: "agentic-gateway/requesting-access",
  },
  filters: GoogleDriveAiFilters,
  identifier: (component) =>
    component === "connector" ? "Google Cloud project" : "Drive identifier",
  itemKind: (component) =>
    component === "connector" ? "Project" : "Shared drive",
  label: "Google Drive",
  logoUrl: GoogleDriveAiIconUrl,
  maturity: "beta",
  // The agent's request routes by the CLI's resource-type positional, so
  // `p0 request google-drive-ai resource …` only works if this is requestable.
  requestType: "requestable",
  useMapping: [{ use: "iam_write", component: "shared-drive" }],
});

registerIntegration("google-drive-ai", GoogleDriveAiRegistration);
